import { Col, Row, Spinner2, Text } from 'components';
import { Api, formatCurrencyAmount } from 'lib';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from 'screens/Admin/components';
import ManualAuditTab from './ManualAuditTab';
import {COUNTRY_OPTIONS, formatPercent, getDefaultDateRange} from './utils'

const TAB_REVENUE_SPLIT = 'revenueSplit';
const TAB_MANUAL_AUDIT = 'manualAudit';

const buildQueryString = ({ startDate, endDate, countries, teamId, interval }) => {
  const params = new URLSearchParams();

  if (startDate) {
    params.set('startDate', `${startDate}T00:00:00.000Z`);
  }

  if (endDate) {
    params.set('endDate', `${endDate}T23:59:59.999Z`);
  }

  if (countries.length) {
    params.set('countryCodes', countries.join(','));
  }

  if (teamId) {
    params.set('teamIds', teamId);
  }

  if (interval) {
    params.set('interval', interval);
  }

  return params.toString();
};

const ConsistencyBadge = ({ row, t }) => {
  const isValid = row?.isSplitConsistent && row?.isPlanShareConsistent;

  if (isValid) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        {t('subscriptions.admin.revenueSplit.consistency.ok', { defaultValue: '100% OK' })}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
      {t('subscriptions.admin.revenueSplit.consistency.warning', {
        defaultValue: 'Check split'
      })}
    </span>
  );
};

const SplitBar = ({ row, t }) => {
  const p4p = Math.max(0, Number(row?.p4pPercentWeighted || 0));
  const team = Math.max(0, Number(row?.teamPercentWeighted || 0));
  const vat = Math.max(0, Number(row?.vatPercentWeighted || 0));

  return (
    <div className="w-full">
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden flex border border-slate-200">
        <div className="bg-sky-500" style={{ width: `${p4p}%` }} />
        <div className="bg-emerald-500" style={{ width: `${team}%` }} />
        <div className="bg-amber-500" style={{ width: `${vat}%` }} />
      </div>
      <div className="mt-1 text-[10px] text-slate-500">
        {t('subscriptions.admin.revenueSplit.splitLegend.p4p', { defaultValue: 'P4P' })}{' '}
        {formatPercent(p4p)} ·{' '}
        {t('subscriptions.admin.revenueSplit.splitLegend.team', { defaultValue: 'Team' })}{' '}
        {formatPercent(team)} ·{' '}
        {t('subscriptions.admin.revenueSplit.splitLegend.vat', { defaultValue: 'VAT' })}{' '}
        {formatPercent(vat)}
      </div>
    </div>
  );
};


const RevenueSplit = () => {
  const { t } = useTranslation();
  const defaults = useMemo(() => getDefaultDateRange(), []);
  const [selectedTab, setSelectedTab] = useState(TAB_REVENUE_SPLIT);

  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [showCountryFilter, setShowCountryFilter] = useState(false);
  const [interval, setInterval] = useState('');
  const countryFilterRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    if (!showCountryFilter) return;
    function handleOutsideClick(e) {
      if (countryFilterRef.current && !countryFilterRef.current.contains(e.target)) {
        setShowCountryFilter(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showCountryFilter]);

  useEffect(() => {
    fetchTeamsByCountries();
  }, [selectedCountries.join(',')]);

  useEffect(() => {
    fetchReport();
  }, [startDate, endDate, selectedCountries.join(','), selectedTeamId, interval]);

  async function fetchTeamsByCountries() {
    setSelectedTeamId('');

    if (!selectedCountries.length) {
      setTeams([]);
      return;
    }

    setLoadingTeams(true);

    try {
      const responses = await Promise.all(
        selectedCountries.map((countryCode) =>
          Api.get(`/admin/getTeams?countryCode=${countryCode}`)
        )
      );

      const deduped = new Map();
      responses.forEach((res) => {
        if (!Array.isArray(res)) return;
        res.forEach((team) => {
          if (!team?._id) return;
          deduped.set(team._id, team);
        });
      });

      setTeams(
        Array.from(deduped.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      );
    } finally {
      setLoadingTeams(false);
    }
  }

  async function fetchReport() {
    setLoading(true);
    setError(false);

    const query = buildQueryString({
      startDate,
      endDate,
      countries: selectedCountries,
      teamId: selectedTeamId,
      interval
    });

    try {
      const res = await Api.get(`/admin/subscriptionPlans/splitReport?${query}`);

      if (res?.data) {
        setReport(res.data);
      } else {
        setReport(null);
        setError(true);
      }
    } catch {
      setReport(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  async function downloadCsv() {
    setDownloading(true);

    const query = buildQueryString({
      startDate,
      endDate,
      countries: selectedCountries,
      teamId: selectedTeamId,
      interval
    });

    try {
      const blob = await Api.getBlob(`/admin/subscriptionPlans/splitReport/csv?${query}`);

      if (!blob || !(blob instanceof Blob)) {
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `revenue-split-report-${startDate}-${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  const currencySections = report?.currencies || [];

  return (
    <Col noFlex wid="100%" ht="100vh" start>
      <Header>
        <Row center between wid="100%">
          <Text h3>
            {selectedTab === TAB_MANUAL_AUDIT
              ? t('subscriptions.admin.revenueSplit.manualTransferAudit.title', {
                  defaultValue: 'Manual Transfer Audit'
                })
              : t('subscriptions.admin.revenueSplit.title', {
                  defaultValue: 'Revenue Split'
                })}
          </Text>
        </Row>
      </Header>

      <div className="mt-[54px] h-[calc(100vh-54px)] flex flex-col bg-[#F8FAFC]">
        {/* Tab switcher */}
        <div className="flex border-b border-[#E2E8F0] bg-white shrink-0">
          <button
            type="button"
            className={`px-6 py-3 text-[13px] font-semibold border-b-2 transition-colors ${selectedTab === TAB_REVENUE_SPLIT ? 'border-[#0F172A] text-[#0F172A]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}
            onClick={() => setSelectedTab(TAB_REVENUE_SPLIT)}
          >
            {t('subscriptions.admin.revenueSplit.manualTransferAudit.tab.revenueSplit', {
              defaultValue: 'Revenue Split'
            })}
          </button>
          <button
            type="button"
            className={`px-6 py-3 text-[13px] font-semibold border-b-2 transition-colors ${selectedTab === TAB_MANUAL_AUDIT ? 'border-[#0F172A] text-[#0F172A]' : 'border-transparent text-[#64748B] hover:text-[#0F172A]'}`}
            onClick={() => setSelectedTab(TAB_MANUAL_AUDIT)}
          >
            {t('subscriptions.admin.revenueSplit.manualTransferAudit.tab.manualAudit', {
              defaultValue: 'Manual Transfer Audit'
            })}
          </button>
        </div>

        {selectedTab === TAB_MANUAL_AUDIT && (
          <div className="flex-1 overflow-hidden flex flex-col">
            <ManualAuditTab />
          </div>
        )}

        {selectedTab === TAB_REVENUE_SPLIT && (
          <>
            <div className="relative z-50 border-b border-[#E2E8F0] bg-white px-6 py-4 shrink-0">
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-[repeat(5,minmax(0,1fr))_auto] gap-3 items-end">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                    {t('subscriptions.admin.revenueSplit.filters.startDate', {
                      defaultValue: 'Start date'
                    })}
                  </span>
                  <input
                    type="date"
                    className="h-10 rounded-lg border border-[#CBD5E1] px-3 text-[13px] text-[#334155] bg-white"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                    {t('subscriptions.admin.revenueSplit.filters.endDate', {
                      defaultValue: 'End date'
                    })}
                  </span>
                  <input
                    type="date"
                    className="h-10 rounded-lg border border-[#CBD5E1] px-3 text-[13px] text-[#334155] bg-white"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </label>

                <div className="relative" ref={countryFilterRef}>
                  <label className="flex flex-col gap-1">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                      {t('subscriptions.admin.revenueSplit.filters.countries', {
                        defaultValue: 'Countries'
                      })}
                    </span>
                    <button
                      type="button"
                      className="h-10 rounded-lg px-3 text-left text-[13px] text-[#334155] bg-white"
                      style={{ border: '1px solid #CBD5E1' }}
                      onClick={() => setShowCountryFilter((prev) => !prev)}
                    >
                      {selectedCountries.length
                        ? t('subscriptions.admin.revenueSplit.filters.selectedCountries', {
                            defaultValue: '{{count}} selected',
                            count: selectedCountries.length
                          })
                        : t('subscriptions.admin.revenueSplit.filters.selectCountries', {
                            defaultValue: 'Select countries'
                          })}
                    </button>
                  </label>

                  {showCountryFilter && (
                    <div className="absolute z-30 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-[#CBD5E1] bg-white shadow-lg p-2 space-y-1">
                      {COUNTRY_OPTIONS.map((country) => {
                        const checked = selectedCountries.includes(country.code);
                        return (
                          <label
                            key={country.code}
                            className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#F1F5F9] cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCountries((prev) => [...prev, country.code]);
                                } else {
                                  setSelectedCountries((prev) =>
                                    prev.filter((item) => item !== country.code)
                                  );
                                }
                              }}
                            />
                            <span className="text-[13px] text-[#334155]">{t(country.label)}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                    {t('subscriptions.admin.revenueSplit.filters.team', { defaultValue: 'Team' })}
                  </span>
                  <select
                    className="h-10 rounded-lg border border-[#CBD5E1] px-3 text-[13px] text-[#334155] bg-white disabled:bg-[#F8FAFC]"
                    value={selectedTeamId}
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                    disabled={!selectedCountries.length || loadingTeams}
                  >
                    <option value="">
                      {loadingTeams
                        ? t('subscriptions.admin.revenueSplit.filters.loadingTeams', {
                            defaultValue: 'Loading teams...'
                          })
                        : t('subscriptions.admin.revenueSplit.filters.allTeams', {
                            defaultValue: 'All teams'
                          })}
                    </option>
                    {teams.map((team) => (
                      <option key={team._id} value={team._id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                    {t('subscriptions.admin.revenueSplit.filters.interval', {
                      defaultValue: 'Interval'
                    })}
                  </span>
                  <select
                    className="h-10 rounded-lg border border-[#CBD5E1] px-3 text-[13px] text-[#334155] bg-white"
                    value={interval}
                    onChange={(e) => setInterval(e.target.value)}
                  >
                    <option value="">
                      {t('subscriptions.admin.revenueSplit.filters.all', { defaultValue: 'All' })}
                    </option>
                    <option value="monthly">
                      {t('subscriptions.admin.revenueSplit.filters.monthly', {
                        defaultValue: 'Monthly'
                      })}
                    </option>
                    <option value="yearly">
                      {t('subscriptions.admin.revenueSplit.filters.yearly', {
                        defaultValue: 'Yearly'
                      })}
                    </option>
                  </select>
                </label>

                <button
                  type="button"
                  className="h-10 px-4 rounded-lg bg-[#0F172A] text-white text-[13px] font-semibold hover:bg-[#1E293B] disabled:opacity-50 min-w-[130px] whitespace-nowrap"
                  onClick={downloadCsv}
                  disabled={downloading || loading}
                >
                  {downloading
                    ? t('subscriptions.admin.revenueSplit.filters.downloading', {
                        defaultValue: 'Downloading...'
                      })
                    : t('subscriptions.admin.revenueSplit.filters.exportCsv', {
                        defaultValue: 'Export CSV'
                      })}
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1">
              {loading && (
                <Col wid="100%" marg="80px 0" centerAll noFlex>
                  <Spinner2 />
                </Col>
              )}

              {!loading && error && (
                <Col wid="100%" marg="80px 0" centerAll noFlex>
                  <Text className="text-[13px] text-red-600">
                    {t('subscriptions.admin.revenueSplit.error', {
                      defaultValue: 'Failed to load revenue split report.'
                    })}
                  </Text>
                </Col>
              )}

              {!loading && !error && !currencySections.length && (
                <Col wid="100%" marg="80px 0" centerAll noFlex>
                  <Text className="text-[13px] text-[#64748B]">
                    {t('subscriptions.admin.revenueSplit.empty', {
                      defaultValue: 'No data found for the selected filters.'
                    })}
                  </Text>
                </Col>
              )}

              {!loading && !error && currencySections.length > 0 && (
                <div className="space-y-6 p-6">
                  {currencySections.map((section) => (
                    <div
                      key={section.currency}
                      className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden"
                    >
                      <div className="px-5 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC]">
                        <Text className="text-[14px] font-semibold text-[#0F172A]">
                          {t('subscriptions.admin.revenueSplit.currencySection', {
                            defaultValue: '{{currency}} Section',
                            currency: section.currency
                          })}
                        </Text>
                      </div>

                      <div className="overflow-auto max-h-[420px]">
                        <table className="min-w-[1580px] w-full border-separate border-spacing-0">
                          <thead className="sticky top-0 z-10 bg-white">
                            <tr>
                              {[
                                'team',
                                'country',
                                'currency',
                                'grossRevenue',
                                'stripeFee',
                                'netRevenue',
                                'splitVisual',
                                'p4pPercent',
                                'p4pAmount',
                                'teamPercent',
                                'teamAmount',
                                'vatPercent',
                                'vatAmount',
                                'plans'
                              ].map((columnKey) => (
                                <th
                                  key={columnKey}
                                  className={`px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-[#64748B] border-b border-[#E2E8F0] bg-white text-center`}
                                >
                                  {t(`subscriptions.admin.revenueSplit.table.${columnKey}`, {
                                    defaultValue: columnKey
                                  })}
                                </th>
                              ))}
                            </tr>
                          </thead>

                          <tbody>
                            {section.rows.map((row) => {
                              const rowKey = `${section.currency}:${row.teamId}`;
                              const isExpanded = !!expandedRows[rowKey];

                              return (
                                <Fragment key={rowKey}>
                                  <tr className="hover:bg-[#F8FAFC]">
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] pl-5 text-left">
                                      <div className="flex items-center gap-2">
                                        <button
                                          type="button"
                                          className="w-4 h-4 text-xl rounded border border-[#CBD5E1] text-[11px] text-[#334155]"
                                          onClick={() =>
                                            setExpandedRows((prev) => ({
                                              ...prev,
                                              [rowKey]: !prev[rowKey]
                                            }))
                                          }
                                        >
                                          {isExpanded ? '−' : '+'}
                                        </button>
                                        <div>
                                          <div className="text-[13px] font-semibold text-[#0F172A]">
                                            {row.team}
                                          </div>
                                          <div className="mt-1">
                                            <ConsistencyBadge row={row} t={t} />
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] text-center capitalize text-[13px] text-[#334155]">
                                      {row.country}
                                    </td>
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] font-semibold text-[#334155]">
                                      {row.currency}
                                    </td>
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] text-[#0F172A] font-semibold">
                                      {formatCurrencyAmount(row.grossRevenue, row.currency)}
                                    </td>
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] text-red-600">
                                      {row.stripeFeeAmount > 0
                                        ? `−${formatCurrencyAmount(row.stripeFeeAmount, row.currency)}`
                                        : '—'}
                                    </td>
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] text-emerald-700 font-semibold">
                                      {formatCurrencyAmount(row.netRevenue, row.currency)}
                                    </td>
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] min-w-[220px] text-center">
                                      <SplitBar row={row} t={t} />
                                    </td>
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] text-[#334155]">
                                      {formatPercent(row.p4pPercentWeighted)}
                                    </td>
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] text-[#334155]">
                                      {formatCurrencyAmount(row.p4pAmount, row.currency)}
                                    </td>
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] text-[#334155]">
                                      {formatPercent(row.teamPercentWeighted)}
                                    </td>
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] text-[#334155]">
                                      {formatCurrencyAmount(row.teamAmount, row.currency)}
                                    </td>
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] text-[#334155]">
                                      {formatPercent(row.vatPercentWeighted)}
                                    </td>
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] text-[#334155]">
                                      {formatCurrencyAmount(row.vatAmount, row.currency)}
                                    </td>
                                    <td className="px-3 py-3 border-b border-[#E2E8F0] text-center text-[13px] text-[#334155]">
                                      {row.plans}
                                    </td>
                                  </tr>

                                  {isExpanded && (
                                    <tr>
                                      <td
                                        colSpan={14}
                                        className="px-5 py-4 bg-[#F8FAFC] border-b border-[#E2E8F0]"
                                      >
                                        <div className="rounded-lg border border-[#E2E8F0] bg-white overflow-hidden">
                                          <table className="w-full">
                                            <thead>
                                              <tr className="bg-[#F8FAFC]">
                                                {[
                                                  'plan',
                                                  'revenueShare',
                                                  'p4p',
                                                  'team',
                                                  'vat',
                                                  'consistency'
                                                ].map((columnKey) => (
                                                  <th
                                                    key={columnKey}
                                                    className="px-3 py-2 text-[11px] uppercase tracking-wide text-[#64748B] text-right first:text-left"
                                                  >
                                                    {t(
                                                      `subscriptions.admin.revenueSplit.planTable.${columnKey}`,
                                                      {
                                                        defaultValue: columnKey
                                                      }
                                                    )}
                                                  </th>
                                                ))}
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {row.planSplits.map((plan) => (
                                                <tr
                                                  key={`${rowKey}:${plan.planId || plan.planName}`}
                                                >
                                                  <td className="px-3 py-2 text-[13px] text-[#0F172A] text-left border-t border-[#E2E8F0]">
                                                    {plan.planName}
                                                  </td>
                                                  <td className="px-3 py-2 text-[13px] text-[#334155] text-right border-t border-[#E2E8F0]">
                                                    {formatPercent(plan.revenueSharePercent)}
                                                  </td>
                                                  <td className="px-3 py-2 text-[13px] text-[#334155] text-right border-t border-[#E2E8F0]">
                                                    {formatPercent(plan.p4p)}
                                                  </td>
                                                  <td className="px-3 py-2 text-[13px] text-[#334155] text-right border-t border-[#E2E8F0]">
                                                    {formatPercent(plan.team)}
                                                  </td>
                                                  <td className="px-3 py-2 text-[13px] text-[#334155] text-right border-t border-[#E2E8F0]">
                                                    {formatPercent(plan.vat)}
                                                  </td>
                                                  <td className="px-3 py-2 text-[13px] text-right border-t border-[#E2E8F0]">
                                                    {plan.isSplitConsistent ? (
                                                      <span className="text-emerald-700">
                                                        {t(
                                                          'subscriptions.admin.revenueSplit.consistency.ok',
                                                          {
                                                            defaultValue: '100% OK'
                                                          }
                                                        )}
                                                      </span>
                                                    ) : (
                                                      <span className="text-red-700">
                                                        {t(
                                                          'subscriptions.admin.revenueSplit.consistency.warning',
                                                          {
                                                            defaultValue: 'Check split'
                                                          }
                                                        )}
                                                      </span>
                                                    )}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </Fragment>
                              );
                            })}
                          </tbody>

                          <tfoot className="sticky bottom-0 bg-[#0F172A]">
                            <tr>
                              <td
                                className="px-3 py-3 text-left pl-5 text-[12px] font-semibold text-white border-t border-[#1E293B]"
                                colSpan={3}
                              >
                                {t('subscriptions.admin.revenueSplit.totals.label', {
                                  defaultValue: '{{currency}} TOTAL',
                                  currency: section.currency
                                })}
                              </td>
                              <td className="px-3 py-3 text-center text-[12px] font-semibold text-white border-t border-[#1E293B]">
                                {formatCurrencyAmount(
                                  section.totals.grossRevenue,
                                  section.currency
                                )}
                              </td>
                              <td className="px-3 py-3 text-center text-[12px] font-semibold text-red-400 border-t border-[#1E293B]">
                                {section.totals.stripeFeeAmount > 0
                                  ? `−${formatCurrencyAmount(section.totals.stripeFeeAmount, section.currency)}`
                                  : '—'}
                              </td>
                              <td className="px-3 py-3 text-center text-[12px] font-semibold text-emerald-400 border-t border-[#1E293B]">
                                {formatCurrencyAmount(section.totals.netRevenue, section.currency)}
                              </td>
                              <td className="px-3 py-3 border-t border-[#1E293B]" />
                              <td className="px-3 py-3 border-t border-[#1E293B]" />
                              <td className="px-3 py-3 text-center text-[12px] font-semibold text-white border-t border-[#1E293B]">
                                {formatCurrencyAmount(section.totals.p4pAmount, section.currency)}
                              </td>
                              <td className="px-3 py-3 border-t border-[#1E293B]" />
                              <td className="px-3 py-3 text-center text-[12px] font-semibold text-white border-t border-[#1E293B]">
                                {formatCurrencyAmount(section.totals.teamAmount, section.currency)}
                              </td>
                              <td className="px-3 py-3 border-t border-[#1E293B]" />
                              <td className="px-3 py-3 text-center text-[12px] font-semibold text-white border-t border-[#1E293B]">
                                {formatCurrencyAmount(section.totals.vatAmount, section.currency)}
                              </td>
                              <td className="px-3 py-3 border-t border-[#1E293B]" />
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Col>
  );
};

export default RevenueSplit;
